import {
  ActionIcon,
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconEyeSearch, IconTrash } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import {
  deleteAdminCreditNote,
  getCreditInvoices,
} from "api/creditnote.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { confirmDelete, formatDate, formatNZD } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import Reprint from "pages/admin/components/Reprint";
import { FullScreenModal } from "pages/modals";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginatedCreditNotes } from "types/model.types";
import AdminCreditNoteEntryPage from "./AdminCreditNoteEntryPage";
import AdminCreditNotePreview from "./AdminCreditNotePreview";
import { DefaultTableLayout } from "./TableGridDefinitions";

const AdminCreditNotePage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [recordId, setRecordId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [creditInvoices, setCreditInvoices] = useState<PaginatedCreditNotes>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  const [entryModalOpen, setEntryModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getCreditInvoices(activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted && response) setCreditInvoices(response);
      } catch (err) {
        logError(err);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [activePage, reloadTrigger]);

  const handlePrint = (id: string | number) => {
    navigateTo(`/print/crnote?id=${id}`);
  };

  const handleEdit = async (id: string | number) => {
    setRecordId(Number(id));
    setEntryModalOpen(true);
  };

  const handleDelete = async (id: string | number) => {
    if (!id) return;
    if (confirmDelete() === false) return;

    try {
      await safeApiCall(deleteAdminCreditNote(Number(id)));
      setReloadTrigger(new Date());
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  useEffect(() => {
    if (!id) return;

    handleEdit(id);
    setTimeout(() => {
      navigate("/admin/credit-notes", { replace: true });
    }, 500);
  }, [id]);

  const rows = creditInvoices.data.map((item) => {
    return (
      <Table.Tr key={item.id}>
        <Table.Td className="action-cell action-cell-lg">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print">
              <ActionIcon variant="subtle" onClick={() => handlePrint(item.id)}>
                <IconEyeSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Edit (disabled)" : "Edit"}>
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={item.isProcessed}
                onClick={() => handleEdit(item.id)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={item.isProcessed ? "Delete (disabled)" : "Delete"}>
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={item.isProcessed}
                onClick={() => handleDelete(item.id)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td>{item.creditTo}</Table.Td>
        <Table.Td>{item.invoiceId}</Table.Td>
        <Table.Td className="money">{formatNZD(item.amount)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.gst)}</Table.Td>
        <Table.Td className="money">{formatNZD(item.total)}</Table.Td>
        <Table.Td className="notes">{item.notes}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Credit Notes</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Group justify="flex-start" wrap="nowrap" mb="md">
              <Reprint
                onReprint={(id: number) => {
                  handlePrint(id);
                }}
              />
              <DefaultButtonAdd
                text=" Add Credit Note"
                onClick={() => {
                  setRecordId(null);
                  setEntryModalOpen(true);
                }}
              />
            </Group>

            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(creditInvoices.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <DefaultTableLayout>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Credit #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Credit To</Table.Th>
                <Table.Th>Invoice #</Table.Th>
                <Table.Th className="money">Amount</Table.Th>
                <Table.Th className="money">GST</Table.Th>
                <Table.Th className="money">Total</Table.Th>
                <Table.Th>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </DefaultTableLayout>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(creditInvoices.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}

      <FullScreenModal
        title="Add|Edit Credit Note Entry"
        closeOnEscape={false}
        opened={entryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setReloadTrigger(new Date());
        }}
      >
        <AdminCreditNoteEntryPage
          recordId={recordId}
          onSaveComplete={(close: boolean) => {
            setReloadTrigger(new Date());
            if (close) setEntryModalOpen(false);
          }}
        />
      </FullScreenModal>

      <FullScreenModal opened={preview} onClose={() => setPreview(false)}>
        {recordId && <AdminCreditNotePreview id={recordId} />}
      </FullScreenModal>
    </Stack>
  );
};
export default AdminCreditNotePage;
